import React, { useState } from 'react';
import { ArrowLeft, ArrowRight, Upload } from 'lucide-react';
import { Card } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { Input } from '../components/common/Input';
import { Select } from '../components/common/Select';
import { FileUpload } from '../components/distribution/FileUpload';
import { StepIndicator } from '../components/distribution/StepIndicator';
import { StoreSelector } from '../components/distribution/StoreSelector';
import { useToast } from '../components/common/Toast';
import { mockArtists, genres } from '../data/mockData';

interface FormData {
  // Step 1: Basic Information
  title: string;
  artistId: string;
  releaseType: 'single' | 'album';
  genre: string;
  releaseDate: string;
  copyright: string;
  
  // Step 2: Media Files
  coverArt?: File;
  audioFile?: File;
  
  // Step 3: Store Selection
  selectedStores: string[];
  
  // Step 4: Metadata
  lyrics: string;
  producer: string;
  featuredArtists: string;
  isrc: string;
  previewStartTime: number;
}

const steps = [
  { id: 1, title: 'Basic Info', description: 'Song details' },
  { id: 2, title: 'Media Upload', description: 'Cover art & audio' },
  { id: 3, title: 'Distribution', description: 'Select stores' },
  { id: 4, title: 'Metadata', description: 'Additional details' },
  { id: 5, title: 'Review', description: 'Confirm & submit' }
];

export const DistributeMusic: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [formData, setFormData] = useState<FormData>({
    title: '',
    artistId: '',
    releaseType: 'single',
    genre: '',
    releaseDate: '',
    copyright: '',
    selectedStores: [],
    lyrics: '',
    producer: '',
    featuredArtists: '',
    isrc: '',
    previewStartTime: 0
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { addToast } = useToast();

  const updateFormData = (field: keyof FormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};
    
    switch (step) {
      case 1:
        if (!formData.title) newErrors.title = 'Song title is required';
        if (!formData.artistId) newErrors.artistId = 'Artist selection is required';
        if (!formData.genre) newErrors.genre = 'Genre is required';
        if (!formData.releaseDate) newErrors.releaseDate = 'Release date is required';
        if (!formData.copyright) newErrors.copyright = 'Copyright information is required';
        break;
      case 2:
        if (!formData.coverArt) newErrors.coverArt = 'Cover art is required';
        if (!formData.audioFile) newErrors.audioFile = 'Audio file is required';
        break;
      case 3:
        if (formData.selectedStores.length === 0) {
          newErrors.selectedStores = 'Please select at least one distribution store';
        }
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (!completedSteps.includes(currentStep)) {
        setCompletedSteps(prev => [...prev, currentStep]);
      }
      setCurrentStep(prev => Math.min(prev + 1, 5));
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = () => {
    if (validateStep(4)) {
      addToast({
        type: 'success',
        title: 'Distribution Started!',
        message: `"${formData.title}" has been submitted for distribution to ${formData.selectedStores.length} stores.`
      });
      
      // Reset form or redirect
      setCurrentStep(1);
      setCompletedSteps([]);
      setFormData({
        title: '',
        artistId: '',
        releaseType: 'single',
        genre: '',
        releaseDate: '',
        copyright: '',
        selectedStores: [],
        lyrics: '',
        producer: '',
        featuredArtists: '',
        isrc: '',
        previewStartTime: 0
      });
    }
  };

  const generateISRC = () => {
    const code = `IN-NMG-24-${Math.random().toString().substr(2, 5)}`;
    updateFormData('isrc', code);
  };

  const artistOptions = mockArtists.map(artist => ({
    value: artist.id,
    label: artist.name
  }));

  const genreOptions = genres.map(genre => ({
    value: genre,
    label: genre
  }));

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Song Title"
                placeholder="Enter song title"
                value={formData.title}
                onChange={(value) => updateFormData('title', value)}
                error={errors.title}
                required
              />
              <Select
                label="Artist"
                value={formData.artistId}
                onChange={(value) => updateFormData('artistId', value)}
                options={artistOptions}
                placeholder="Select artist"
                error={errors.artistId}
                required
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Select
                label="Release Type"
                value={formData.releaseType}
                onChange={(value) => updateFormData('releaseType', value)}
                options={[
                  { value: 'single', label: 'Single' },
                  { value: 'album', label: 'Album' }
                ]}
              />
              <Select
                label="Genre"
                value={formData.genre}
                onChange={(value) => updateFormData('genre', value)}
                options={genreOptions}
                placeholder="Select genre"
                error={errors.genre}
                required
              />
              <Input
                label="Release Date"
                type="date"
                value={formData.releaseDate}
                onChange={(value) => updateFormData('releaseDate', value)}
                error={errors.releaseDate}
                required
              />
            </div>
            
            <Input
              label="Copyright Information"
              placeholder="e.g., 2024 Naad Music Group"
              value={formData.copyright}
              onChange={(value) => updateFormData('copyright', value)}
              error={errors.copyright}
              required
            />
          </div>
        );
        
      case 2:
        return (
          <div className="space-y-8">
            <FileUpload
              label="Cover Art"
              accept="image/*"
              type="image"
              value={formData.coverArt}
              onChange={(file) => updateFormData('coverArt', file)}
              error={errors.coverArt}
              requirements={[
                'Minimum 1400x1400 pixels',
                'Square aspect ratio (1:1)',
                'JPG or PNG format',
                'Maximum file size: 10MB',
                'No text overlay or borders'
              ]}
            />
            
            <FileUpload
              label="Audio File"
              accept="audio/*"
              type="audio"
              value={formData.audioFile}
              onChange={(file) => updateFormData('audioFile', file)}
              error={errors.audioFile}
              requirements={[
                'WAV or MP3 format',
                'Minimum 320kbps quality',
                'Maximum file size: 100MB',
                'No silence at beginning or end',
                'Proper mastering and loudness'
              ]}
            />
          </div>
        );
        
      case 3:
        return (
          <div>
            <StoreSelector
              selectedStores={formData.selectedStores}
              onChange={(stores) => updateFormData('selectedStores', stores)}
            />
            {errors.selectedStores && (
              <p className="text-red-400 text-sm mt-4">{errors.selectedStores}</p>
            )}
          </div>
        );
        
      case 4:
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Producer"
                placeholder="Enter producer name"
                value={formData.producer}
                onChange={(value) => updateFormData('producer', value)}
              />
              <Input
                label="Featured Artists"
                placeholder="Enter featured artists (comma separated)"
                value={formData.featuredArtists}
                onChange={(value) => updateFormData('featuredArtists', value)}
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex space-x-2">
                <Input
                  label="ISRC Code"
                  placeholder="IN-NMG-24-00000"
                  value={formData.isrc}
                  onChange={(value) => updateFormData('isrc', value)}
                />
                <div className="flex items-end">
                  <Button
                    variant="outline"
                    onClick={generateISRC}
                    className="mb-0"
                  >
                    Generate
                  </Button>
                </div>
              </div>
              <Input
                label="Preview Start Time (seconds)"
                type="number"
                placeholder="30"
                value={formData.previewStartTime.toString()}
                onChange={(value) => updateFormData('previewStartTime', parseInt(value) || 0)}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Lyrics (Optional)
              </label>
              <textarea
                className="w-full px-3 py-2 bg-black-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gold-500 focus:border-transparent transition-all duration-200"
                rows={6}
                placeholder="Enter song lyrics..."
                value={formData.lyrics}
                onChange={(e) => updateFormData('lyrics', e.target.value)}
              />
            </div>
          </div>
        );
        
      case 5:
        return (
          <div className="space-y-6">
            <Card>
              <h3 className="text-lg font-semibold text-white font-display mb-4">
                Review Your Submission
              </h3>
              
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-400">Song Title</p>
                    <p className="text-white font-medium">{formData.title}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Artist</p>
                    <p className="text-white font-medium">
                      {mockArtists.find(a => a.id === formData.artistId)?.name}
                    </p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-gray-400">Genre</p>
                    <p className="text-white font-medium">{formData.genre}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Release Date</p>
                    <p className="text-white font-medium">{formData.releaseDate}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Distribution Stores</p>
                    <p className="text-white font-medium">{formData.selectedStores.length} stores</p>
                  </div>
                </div>
                
                {formData.coverArt && formData.audioFile && (
                  <div className="flex items-center space-x-4 pt-4 border-t border-gray-700">
                    <div className="text-green-400 text-sm">✓ Cover art uploaded</div>
                    <div className="text-green-400 text-sm">✓ Audio file uploaded</div>
                  </div>
                )}
              </div>
            </Card>
            
            <Card className="bg-yellow-500/10 border-yellow-500/20">
              <h4 className="text-yellow-400 font-medium mb-2">Terms & Conditions</h4>
              <p className="text-sm text-gray-300 mb-4">
                By submitting this content, you confirm that you own all rights to this music 
                and authorize Naad Music Group to distribute it to the selected platforms.
              </p>
              <label className="flex items-center space-x-2">
                <input type="checkbox" className="rounded border-gray-600 bg-black-700 text-gold-500 focus:ring-gold-500" />
                <span className="text-sm text-gray-300">I agree to the terms and conditions</span>
              </label>
            </Card>
          </div>
        );
        
      default:
        return null;
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <StepIndicator
        steps={steps}
        currentStep={currentStep}
        completedSteps={completedSteps}
      />
      
      <Card className="p-8">
        {renderStepContent()}
        
        <div className="flex justify-between mt-8 pt-6 border-t border-gray-700">
          <Button
            variant="outline"
            onClick={handlePrevious}
            disabled={currentStep === 1}
            icon={ArrowLeft}
          >
            Previous
          </Button>
          
          {currentStep === 5 ? (
            <Button
              onClick={handleSubmit}
              icon={Upload}
              iconPosition="right"
            >
              Submit for Distribution
            </Button>
          ) : (
            <Button
              onClick={handleNext}
              icon={ArrowRight}
              iconPosition="right"
            >
              Next Step
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
};